import { ServiceType } from "../../../../pages/attorney-services/utils";
import {
  State,
  DocumentWithRelations,
  PurchaseItem,
  Product,
  QuestionnaireSectionsForNavigation,
} from "../../../../../../api/src/types";
import type { AnswersWithAttorneyRestrictionsGetResponse } from "../../../../../../api/src/users/answers/route";
import type { ProductsGetResponse } from "../../../../../../api/src/products/route";
import { formatCurrency } from "../../../../utils/format";

export type ButtonAction = "BOOK" | "PURCHASE";

export interface AttorneyServiceItem {
  serviceType: ServiceType;
  productId: string;
  serviceName: string;
  serviceDescription: string;
  serviceFooterText: string | null;
  quantity: number;
  buttonAction: ButtonAction;
  buttonText: string;
}

export interface AttorneyServiceData {
  serviceType: ServiceType;
  productId: string;
  serviceName: string;
  defaultDescription: string;
  willHireAttorneyDescription?: string;
  waivedSpousalSupportDescription?: string;
  includedEmbryoClauseDescription?: string;
  footerText: string | null;
  priceInCents: number;
}

export const getAttorneyServiceProductData = (
  serviceType: ServiceType,
  products: ProductsGetResponse,
) => {
  const product = products.find(
    (product) => product.productType === serviceType,
  );
  return {
    productId: product?.id ?? "",
    name: product?.title ?? "",
    priceInCents: product?.priceInCents ?? 0,
  };
};

export const getAttorneyServicesData = (
  state: State,
  products: ProductsGetResponse,
) => {
  const representationProductData = getAttorneyServiceProductData(
    "REPRESENTATION",
    products,
  );
  const qaProductData = getAttorneyServiceProductData("Q_A", products);
  const services: Record<ServiceType, AttorneyServiceData> = {
    REPRESENTATION: {
      serviceType: "REPRESENTATION",
      productId: representationProductData.productId,
      serviceName: representationProductData.name,
      defaultDescription:
        "Want attorneys to review, edit, & sign off on your agreement? Book Representation for you and your partner now!",
      willHireAttorneyDescription: `Because you indicated that you plan to hire an attorney, don't forget that your prenup can't be finalized until your lawyer has signed off. Let's get your attorney booked to keep things moving.`,
      waivedSpousalSupportDescription: `Because you are waiving spousal support, ${state.abbreviation} law requires both you and your partner to hire attorneys to sign off on your agreement & advise you of the rights you are waiving. Let's book your attorneys for this service now.`,
      includedEmbryoClauseDescription: `Because you added Embryo clauses, ${state.abbreviation} law requires both you and your partner to hire attorneys to sign off on your agreement. Let's book your attorneys for this service now`,
      footerText: `${state.abbreviation} requires both parties to have an attorney if one party wants Representation.`,
      priceInCents: representationProductData.priceInCents,
    },
    Q_A: {
      serviceType: "Q_A",
      productId: qaProductData.productId,
      serviceName: qaProductData.name,
      defaultDescription:
        "Get your legal questions answered before completing your prenuptial agreement.",
      priceInCents: qaProductData.priceInCents,
      footerText: null,
    },
  };

  return services;
};

export const getRepresentationTextAndQuantityBasedOnRestrictions = (
  RepresentationService: AttorneyServiceData,
  answersWithAttorneyRestrictions: AnswersWithAttorneyRestrictionsGetResponse,
  documentStateId: string,
) => {
  const attorneyRestrictions = answersWithAttorneyRestrictions.flatMap(
    (answer) =>
      answer.answerQuestionOptions.flatMap((option) =>
        option.questionOption.attorneyRulesByQuestionOptionState.filter(
          (rule) => rule.stateId === documentStateId,
        ),
      ),
  );
  const hasAttorneyRestrictions = attorneyRestrictions.length > 0;
  if (!hasAttorneyRestrictions) {
    return {
      serviceDescription: RepresentationService.defaultDescription,
      serviceFooterText: null,
      quantity: 1,
    };
  }
  const hasWaivedSpousalSupport = attorneyRestrictions.some(
    (restriction) => restriction.type === "WAIVES_SPOUSAL_SUPPORT",
  );
  if (hasWaivedSpousalSupport) {
    return {
      serviceDescription: RepresentationService.waivedSpousalSupportDescription,
      serviceFooterText: RepresentationService.footerText,
      // Can be increased to 2 if we want to allow the user to add more representation products from the button
      quantity: 1,
    };
  }
  const hasIncludedEmbryoClause = attorneyRestrictions.some(
    (restriction) => restriction.type === "INCLUDES_EMBRYO_CLAUSE",
  );
  if (hasIncludedEmbryoClause) {
    return {
      serviceDescription: RepresentationService.includedEmbryoClauseDescription,
      serviceFooterText: RepresentationService.footerText,
      // Can be increased to 2 if we want to allow the user to add more representation products from the button
      quantity: 1,
    };
  }
  const willHireAttorney = attorneyRestrictions.some(
    (restriction) => restriction.type === "HIRES_ATTORNEY",
  );
  if (willHireAttorney) {
    return {
      serviceDescription: RepresentationService.willHireAttorneyDescription,
      serviceFooterText: null,
      quantity: 1,
    };
  }
  return {
    serviceDescription: RepresentationService.defaultDescription,
    serviceFooterText: null,
    quantity: 1,
  };
};

export const getRepresentationQuantityBasedOnEligibility = (
  userCanPurchaseRepresentation: boolean,
  fianceCanPurchaseRepresentation: boolean,
  maxRepresentationProductsCanPurchase: number,
  existingRepresentationProductCount: number,
) => {
  // If both fiances can purchase Representation and the existing count is less than or equal to the max, we can return the existing count.
  if (
    userCanPurchaseRepresentation &&
    fianceCanPurchaseRepresentation &&
    existingRepresentationProductCount <= maxRepresentationProductsCanPurchase
  ) {
    return { quantity: existingRepresentationProductCount, error: null };
  }
  // If both fiances can purchase Representation and the count is greater than the max, we can return the max if it's greater than 0.
  // For now we are returning 1 as the max count, if we want to increase it we can return the max.
  if (
    userCanPurchaseRepresentation &&
    fianceCanPurchaseRepresentation &&
    existingRepresentationProductCount > maxRepresentationProductsCanPurchase &&
    maxRepresentationProductsCanPurchase > 0
  ) {
    return { quantity: 1, error: null };
  }
  // If the user can purchase Representation and the fiance cannot, we can return 1 as long as the max is greater than 0.
  if (
    userCanPurchaseRepresentation &&
    !fianceCanPurchaseRepresentation &&
    maxRepresentationProductsCanPurchase > 0
  ) {
    return { quantity: 1, error: null };
  }
  // If the user cannot purchase Representation and the fiance can, we can return 1 as long as the max is greater than 0.
  if (
    !userCanPurchaseRepresentation &&
    fianceCanPurchaseRepresentation &&
    maxRepresentationProductsCanPurchase > 0
  ) {
    return { quantity: 1, error: null };
  }
  return {
    quantity: 0,
    error:
      "Representation services are currently unavailable. We don't have enough attorneys in your state to provide this service at the moment. Please try again later or contact our support team for assistance.",
  };
};

export const getAttorneyServiceItemPrice = (
  quantity: number,
  priceInCents: number,
) => {
  const price = quantity * priceInCents;
  return formatCurrency(price, "USD");
};

export const getAttorneyServiceItem = (
  service: AttorneyServiceData,
  quantity: number,
  action: ButtonAction,
  customServiceDescription?: string,
  customServiceFooterText?: string,
) => {
  return {
    serviceType: service.serviceType,
    productId: service.productId,
    serviceName: service.serviceName,
    serviceDescription: customServiceDescription ?? service.defaultDescription,
    quantity,
    buttonAction: action,
    serviceFooterText: customServiceFooterText ?? null,
    buttonText:
      action === "BOOK"
        ? `Book your ${service.serviceName}`
        : `Book ${service.serviceName} - ${getAttorneyServiceItemPrice(quantity, service.priceInCents)}`,
  };
};

interface getAttorneyServicesItemsParams {
  products: ProductsGetResponse;
  userDocument: DocumentWithRelations;
  answersWithAttorneyRestrictions: AnswersWithAttorneyRestrictionsGetResponse;
  representationPayment: (PurchaseItem & { product: Product }) | undefined;
  userCanPurchaseRepresentation: boolean;
  fianceCanPurchaseRepresentation: boolean;
  maxRepresentationProductsCanPurchase: number;
  qAPayment: (PurchaseItem & { product: Product }) | undefined;
  isQAPending: boolean;
  isQAProductAvailable: boolean;
}

export const getAttorneyServicesItems = ({
  userDocument,
  products,
  answersWithAttorneyRestrictions,
  representationPayment,
  userCanPurchaseRepresentation,
  fianceCanPurchaseRepresentation,
  maxRepresentationProductsCanPurchase,
  qAPayment,
  isQAPending,
  isQAProductAvailable,
}: getAttorneyServicesItemsParams) => {
  const items: AttorneyServiceItem[] = [];
  const services = getAttorneyServicesData(userDocument.state, products);
  const representationTextAndQuantityBasedOnRestrictions =
    getRepresentationTextAndQuantityBasedOnRestrictions(
      services.REPRESENTATION,
      answersWithAttorneyRestrictions,
      userDocument.stateId,
    );
  if (representationPayment) {
    // User has a pending representation and already generated a document
    if (
      representationPayment.bookingStatus !== "BOOKED" &&
      !!userDocument.lastGeneratedAt
    ) {
      items.push(
        getAttorneyServiceItem(
          services.REPRESENTATION,
          representationTextAndQuantityBasedOnRestrictions.quantity,
          "BOOK",
          representationTextAndQuantityBasedOnRestrictions.serviceDescription,
          representationTextAndQuantityBasedOnRestrictions.serviceFooterText ??
            undefined,
        ),
      );
    }
    if (isQAPending && qAPayment) {
      items.push(getAttorneyServiceItem(services.Q_A, 1, "BOOK"));
    } else if (
      representationPayment.bookingStatus !== "BOOKED" &&
      isQAProductAvailable
    ) {
      items.push(getAttorneyServiceItem(services.Q_A, 1, "PURCHASE"));
    }
    return { items, error: null };
  }

  const { quantity: representationQuantity, error: representationError } =
    getRepresentationQuantityBasedOnEligibility(
      userCanPurchaseRepresentation,
      fianceCanPurchaseRepresentation,
      maxRepresentationProductsCanPurchase,
      representationTextAndQuantityBasedOnRestrictions.quantity,
    );
  // Add representation item if available
  if (representationQuantity > 0) {
    items.push(
      getAttorneyServiceItem(
        services.REPRESENTATION,
        representationQuantity,
        "PURCHASE",
        representationTextAndQuantityBasedOnRestrictions.serviceDescription,
        representationTextAndQuantityBasedOnRestrictions.serviceFooterText ??
          undefined,
      ),
    );
  }

  // Add QA items if available
  if (isQAPending && qAPayment) {
    items.push(getAttorneyServiceItem(services.Q_A, 1, "BOOK"));
  } else if (isQAProductAvailable) {
    items.push(getAttorneyServiceItem(services.Q_A, 1, "PURCHASE"));
  }

  return { items, error: representationError };
};

export const getSectionToReturn = (
  sections: QuestionnaireSectionsForNavigation[],
  answersWithAttorneyRestrictions: AnswersWithAttorneyRestrictionsGetResponse,
  documentStateId: string,
) => {
  const attorneyRestrictions = answersWithAttorneyRestrictions.flatMap(
    (answer) =>
      answer.answerQuestionOptions.flatMap((option) =>
        option.questionOption.attorneyRulesByQuestionOptionState
          .filter((rule) => rule.stateId === documentStateId)
          .map((rule) => ({
            ...rule,
            parentQuestionnaireSectionId:
              answer.question.parentQuestionnaireSectionId,
          })),
      ),
  );
  const sectionsWithRestrictions = sections.filter((section) =>
    attorneyRestrictions.some(
      (restriction) => restriction.parentQuestionnaireSectionId === section.id,
    ),
  );
  const sectionToReturn =
    sectionsWithRestrictions.length > 0 ? sectionsWithRestrictions[0] : null;
  return sectionToReturn;
};
